// src/components/admin/RemesasPanel.jsx
import { useEffect, useState, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import {
  Download,
  Eye,
  Filter,
  PlusCircle,
  Trash2,
  X,
  Settings2,
  Loader2,
  ChevronDown,
  ChevronRight,
  Pencil,
} from "lucide-react";
import Button from "./Button";
import Select from "./Select";
import TextInput from "./TextInput";
import FieldLabel from "./FieldLabel";
import EstadoReciboSelect from "./EstadoReciboSelect";
import { API, fetchBlob, fetchJson, headersAuth } from "../Utiles/api";
import { exportRowsToExcel } from "../Utiles/excel";
import { defaultPeriodoHoy, fmtMoney, todayISO } from "../Utiles/helpers";
import axios from "axios";

/* ===========================
   Helpers: recalcular totales
=========================== */
const n = (v) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
};

const sumImporteRecibos = (recibos = []) =>
  (Array.isArray(recibos) ? recibos : []).reduce((acc, rc) => acc + n(rc?.importe), 0);

const calcLoteStats = (lote) => {
  const recibos = Array.isArray(lote?.recibos) ? lote.recibos : [];
  return {
    nbTx: recibos.length,
    sum: sumImporteRecibos(recibos),
  };
};

const calcDetalleStats = (detalle) => {
  const lotes = Array.isArray(detalle?.lotes) ? detalle.lotes : [];
  let nbTx = 0;
  let sum = 0;
  for (const l of lotes) {
    const s = calcLoteStats(l);
    nbTx += s.nbTx;
    sum += s.sum;
  }
  return { nbTx, sum };
};

const RemesasPanel = ({ toast, setToast }) => {
  const [tipo, setTipo] = useState("GRAL");
  const [periodo, setPeriodo] = useState(defaultPeriodoHoy());
  const [reqdColltnDt, setReqd] = useState(todayISO());
  const [loadingGen, setLoadingGen] = useState(false);

  const [loadingList, setLoadingList] = useState(false);
  const [remesas, setRemesas] = useState([]);
  const [q, setQ] = useState("");

  const [detalle, setDetalle] = useState();
  const [loadingDet, setLoadingDet] = useState(false);

  const [showBulk, setShowBulk] = useState(false);
  const [busyBulk, setBusyBulk] = useState(false);
  const [bulkCodes, setBulkCodes] = useState("");

  const [expandAltas, setExpandAltas] = useState(false);

  const VENT_ENDPOINT = API.ventanillaCalcular || "/api/remesas/ventanilla/calcular";

  // ====== NUEVO: edición / borrado de recibos ======
  const RECIBOS_BASE = "/api/recibos";
  const [editOpen, setEditOpen] = useState(false);
  const [editRc, setEditRc] = useState(null);
  const [savingRc, setSavingRc] = useState(false);
  const [deletingRcId, setDeletingRcId] = useState(null);

  const openEditRecibo = (rc) => {
    setEditRc({
      id: rc.id,
      importe: rc.importe ?? "",
      periodo: rc.periodo ?? "",
      resultado_banco: rc.resultado_banco ?? rc.resultadoBanco ?? "PENDIENTE",
      remesable: toBool(rc.remesable, true),
      mndt_id: rc.mndt_id ?? rc.mndtId ?? "",
      end_to_end_id: rc.end_to_end_id ?? rc.endToEndId ?? "",
      colegiado_codigo: rc.colegiado_codigo ?? rc.colegiadoCodigo ?? "",
    });
    setEditOpen(true);
  };

  const toBool = (v, defaultValue = true) => {
    if (v === null || v === undefined) return defaultValue;
    if (typeof v === "boolean") return v;
    if (typeof v === "number") return v === 1;
    if (typeof v === "string") {
      const s = v.trim().toLowerCase();
      if (["1", "true", "t", "si", "sí", "s", "y", "yes"].includes(s)) return true;
      if (["0", "false", "f", "no", "n"].includes(s)) return false;
    }
    return Boolean(v);
  };

  const closeEditRecibo = () => {
    setEditOpen(false);
    setEditRc(null);
  };

  const saveEditRecibo = async () => {
    if (!editRc?.id) return;
    try {
      setSavingRc(true);

      await fetchJson(`${RECIBOS_BASE}/${editRc.id}`, {
        method: "PATCH",
        headers: { ...headersAuth() },
        body: JSON.stringify({
          importe: editRc.importe === "" ? null : Number(editRc.importe),
          periodo: (editRc.periodo || "").trim(),
          resultadoBanco: editRc.resultado_banco,
          remesable: !!editRc.remesable,
          mndtId: (editRc.mndt_id || "").trim() || null,
          endToEndId: (editRc.end_to_end_id || "").trim() || null,
        }),
      });

      setToast(`Recibo ${editRc.id} actualizado`);
      closeEditRecibo();
      if (detalle?.id) await verDetalle(detalle.id);
    } catch (e) {
      setToast(e.message || "Error editando recibo");
    } finally {
      setSavingRc(false);
    }
  };

  const eliminarRecibo = async (rc) => {
    const id = rc?.id;
    if (!id) return;

    if (!window.confirm(`¿Eliminar el recibo ${id}? Esta acción no se puede deshacer.`)) return;

    try {
      setDeletingRcId(id);

      await fetchJson(`${RECIBOS_BASE}/${id}`, {
        method: "DELETE",
        headers: { ...headersAuth() },
      });

      setToast(`Recibo ${id} eliminado`);
      if (detalle?.id) await verDetalle(detalle.id);
    } catch (e) {
      const msg = e?.message || e?.error || "Error eliminando recibo";
      setToast(msg);
    } finally {
      setDeletingRcId(null);
    }
  };

  const lastParcialId = useMemo(() => {
    const parciales = remesas.filter((r) => (r.tipo || "").toUpperCase() === "PARCIAL");
    if (parciales.length === 0) return null;
    return Math.max(...parciales.map((r) => Number(r.id)));
  }, [remesas]);

  // Agrupados ALTAS
  const altasRemesas = useMemo(
    () => remesas.filter((r) => (r.tipo || "").toUpperCase() === "ALTAS"),
    [remesas]
  );
  const otherRemesas = useMemo(
    () => remesas.filter((r) => (r.tipo || "").toUpperCase() !== "ALTAS"),
    [remesas]
  );

  const altasStats = useMemo(() => {
    const nbTx = altasRemesas.reduce((acc, r) => acc + Number(r.nb_tx ?? r.nbTx ?? 0), 0);
    const sum = altasRemesas.reduce((acc, r) => acc + Number(r.ctrl_sum ?? r.ctrlSum ?? 0), 0);
    const lastCreated = altasRemesas.reduce((acc, r) => {
      const d = new Date(r.creado_el ?? r.creadoEl).getTime();
      return Math.max(acc, isFinite(d) ? d : 0);
    }, 0);
    return { count: altasRemesas.length, nbTx, sum, lastCreated };
  }, [altasRemesas]);

  // === NUEVO: stats “reales” del detalle (desde recibos) ===
  const detalleStats = useMemo(() => calcDetalleStats(detalle), [detalle]);

  const actualizarParcial = async (id) => {
    try {
      if (lastParcialId !== id) {
        setToast("Solo puede actualizarse la última remesa PARCIAL");
        return;
      }
      const res = await fetchJson(API.actualizarParcial(id), { method: "POST" });
      const added = res?.added_tx ?? res?.addedTx ?? 0;
      const sum = res?.added_sum ?? res?.addedSum ?? 0;
      const totalTx = res?.nb_tx ?? res?.nbTx ?? 0;
      const totalSum = res?.ctrl_sum ?? res?.ctrlSum ?? 0;

      setToast(
        added > 0
          ? `Actualizada PARCIAL #${id}: +${added} tx • +${fmtMoney(sum)} € • Total: ${totalTx} tx, ${fmtMoney(totalSum)} €`
          : `PARCIAL #${id} ya estaba al día (0 nuevos recibos)`
      );

      await refreshList();
      if (detalle?.id === id) await verDetalle(id);
    } catch (e) {
      setToast(e.message || "Error actualizando parcial");
    }
  };

  const refreshList = async () => {
    try {
      setLoadingList(true);
      const qs = q ? `?q=${encodeURIComponent(q)}` : "";
      const data = await fetchJson(`${API.listar}${qs}`);
      setRemesas(Array.isArray(data) ? data : []);
    } catch (e) {
      setToast(e.message || "Error cargando remesas");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    refreshList();
  }, []); // eslint-disable-line

  const onGenerar = async () => {
    try {
      setLoadingGen(true);
      if (tipo === "VENTANILLA") {
        const body = { periodo, fechaCobro: reqdColltnDt };
        const data = await fetchJson(VENT_ENDPOINT, { method: "POST", body: JSON.stringify(body) });
        const nbTx = data?.nb_tx ?? data?.nbTx ?? 0;
        const remesaId = data?.remesa_id ?? data?.remesaId ?? null;
        const ctrl = fmtMoney(data?.ctrl_sum ?? data?.ctrlSum);
        if (!remesaId || nbTx === 0) setToast("No se generaron recibos de ventanilla (0 tx)");
        else setToast(`Remesa VENTANILLA #${remesaId}: ${nbTx} tx • Total ${ctrl}`);
        await refreshList();
        return;
      }

      const body = { tipo, periodo, reqd_colltn_dt: reqdColltnDt };
      const data = await fetchJson(API.generar, { method: "POST", body: JSON.stringify(body) });
      const nbTx = data?.nb_tx ?? data?.nbTx ?? 0;
      const remesaId = data?.remesa_id ?? data?.remesaId ?? null;
      const ctrl = fmtMoney(data?.ctrl_sum ?? data?.ctrlSum);
      if (!remesaId || nbTx === 0) setToast("No se generaron cargos (0 tx)");
      else setToast(`Remesa #${remesaId} generada: ${nbTx} tx • Total ${ctrl}`);
      await refreshList();
    } catch (e) {
      setToast(e.message || "Error generando remesa");
    } finally {
      setLoadingGen(false);
    }
  };

  const verDetalle = async (id) => {
    try {
      setLoadingDet(true);
      const data = await fetchJson(API.detalle(id));
      setDetalle(data);
    } catch (e) {
      setToast(e.message || "Error en detalle");
    } finally {
      setLoadingDet(false);
    }
  };

  const descargarXML = async (id) => {
    try {
      const blob = await fetchBlob(API.pain008(id), { headers: { Accept: "application/xml" } });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `remesa_${id}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setToast(e.message || "Error descargando XML");
    }
  };

  const eliminarRemesa = async (id) => {
    if (!confirm(`¿Eliminar la remesa ${id}?`)) return;
    try {
      await fetchJson(API.eliminar(id), { method: "DELETE" });
      setToast(`Remesa ${id} eliminada`);
      if (detalle?.id === id) setDetalle(undefined);
      await refreshList();
    } catch (e) {
      setToast(e.message || "Error eliminando remesa");
    }
  };

  const setResultadoRecibo = async (recibo, valorUi) => {
    try {
      await fetchJson(API.setResultado(recibo.id, valorUi), { method: "PATCH" });
      setToast(`Recibo ${recibo.id}: estado=${valorUi}`);
      setDetalle((d) =>
        d
          ? {
            ...d,
            lotes: d.lotes.map((l) => ({
              ...l,
              recibos: l.recibos.map((rc) =>
                rc.id === recibo.id ? { ...rc, resultado_banco: valorUi } : rc
              ),
            })),
          }
          : d
      );
    } catch (e) {
      setToast(e.message || "Error actualizando estado");
    }
  };

  const toggleRemesable = async (recibo) => {
    try {
      const current = toBool(recibo.remesable, true);
      const next = !current;

      await fetchJson(`/api/recibos/${recibo.id}/remesable`, {
        method: "PATCH",
        headers: { ...headersAuth(), "Content-Type": "application/json" },
        body: JSON.stringify({ remesable: next }),
      });

      setToast(`Recibo ${recibo.id}: remesable=${next}`);

      setDetalle((d) =>
        d
          ? {
            ...d,
            lotes: d.lotes.map((l) => ({
              ...l,
              recibos: l.recibos.map((rc) =>
                rc.id === recibo.id ? { ...rc, remesable: next } : rc
              ),
            })),
          }
          : d
      );
    } catch (e) {
      setToast(e.message || "Error actualizando recibo");
    }
  };

  const parseCodigos = (s) =>
    (s || "")
      .split(/[\s,;]+/)
      .map((x) => x.trim())
      .filter(Boolean)
      .map((x) => Number(x))
      .filter((x) => Number.isFinite(x));

  const getCodigos = () => parseCodigos(bulkCodes);

  const doBulkResultado = async (valor, scope, codigos = []) => {
    if (!detalle?.id) return;
    try {
      setBusyBulk(true);
      await fetchJson(API.bulkResultado(detalle.id), {
        method: "PATCH",
        headers: { ...headersAuth(), "Content-Type": "application/json" },
        body: JSON.stringify({ valor, modo: scope, colegiados: codigos }),
      });
      setToast(`Bulk resultado=${valor} aplicado`);
      await verDetalle(detalle.id);
    } catch (e) {
      setToast(e.message || "Error bulk resultado");
    } finally {
      setBusyBulk(false);
    }
  };

  const doBulkRemesable = async (val, scope, codigos = []) => {
    if (!detalle?.id) return;
    try {
      setBusyBulk(true);
      await fetchJson(API.bulkRemesable(detalle.id), {
        method: "PATCH",
        headers: { ...headersAuth(), "Content-Type": "application/json" },
        body: JSON.stringify({
          remesable: Number(val) === 1 ? 1 : 0,
          modo: scope,
          colegiados: codigos,
        }),
      });
      setToast(`Bulk remesable=${val} aplicado`);
      await verDetalle(detalle.id);
    } catch (e) {
      setToast(e.message || "Error bulk remesable");
    } finally {
      setBusyBulk(false);
    }
  };

  const exportarListadoRemesas = () => {
    exportRowsToExcel({
      filename: "remesas.xlsx",
      rows: remesas,
      columns: [
        { header: "ID", value: (r) => r.id },
        { header: "Tipo", value: (r) => r.tipo },
        { header: "Periodo", value: (r) => r.periodo },
        { header: "Estado", value: (r) => r.estado },
        { header: "Tx", value: (r) => r.nb_tx ?? r.nbTx ?? 0 },
        { header: "Importe", value: (r) => r.ctrl_sum ?? r.ctrlSum ?? 0 },
        {
          header: "Creado",
          value: (r) => (r.creado_el || r.creadoEl ? new Date(r.creado_el ?? r.creadoEl).toLocaleString() : ""),
        },
      ],
    });
  };

  const exportarRecibosDeRemesa = () => {
    if (!detalle?.lotes) return;
    const all = [];
    for (const l of detalle.lotes) {
      for (const rc of l.recibos || []) {
        all.push({
          loteId: l.id,
          seq: l.seq_tp ?? l.seqTp ?? "",
          fechaCobro: l.reqd_colltn_dt ?? l.reqdColltnDt ?? "",
          reciboId: rc.id,
          colegiado: rc.colegiado_codigo ?? rc.colegiadoCodigo ?? "",
          periodo: rc.periodo ?? "",
          importe: rc.importe ?? "",
          estado: rc.resultado_banco ?? rc.resultadoBanco ?? "",
          remesable: !!rc.remesable ? "Sí" : "No",
          mndt: rc.mndt_id ?? rc.mndtId ?? "",
          e2e: rc.end_to_end_id ?? rc.endToEndId ?? "",
        });
      }
    }
    exportRowsToExcel({
      filename: `remesa_${detalle.id}_recibos.xlsx`,
      rows: all,
      columns: [
        { header: "Lote", value: (r) => r.loteId },
        { header: "Seq", value: (r) => r.seq },
        { header: "Fecha cobro", value: (r) => r.fechaCobro },
        { header: "Recibo", value: (r) => r.reciboId },
        { header: "Colegiado", value: (r) => r.colegiado },
        { header: "Periodo", value: (r) => r.periodo },
        { header: "Importe", value: (r) => r.importe },
        { header: "Estado", value: (r) => r.estado },
        { header: "Remesable", value: (r) => r.remesable },
        { header: "Mandato", value: (r) => r.mndt },
        { header: "E2E", value: (r) => r.e2e },
      ],
    });
  };

  // ====================== Prefetch de nombres ======================
  const API_BASE = typeof API === "string" ? API : process.env.REACT_APP_API_URL || "";
  const colegiadoCache = useRef(new Map());
  const [colegiadoNames, setColegiadoNames] = useState({});

  const nombreCompleto = (c) => [c?.nombre, c?.apellido_1, c?.apellido_2].filter(Boolean).join(" ").trim();

  const fetchColegiadoDetalles = async (codigoColegiado) => {
    if (!codigoColegiado) return null;
    if (colegiadoCache.current.has(codigoColegiado)) return colegiadoCache.current.get(codigoColegiado);

    try {
      const { data } = await axios.get(`${API_BASE}/api/admin/colegiados/${codigoColegiado}`, {
        headers: headersAuth(),
      });
      const c = {
        nombre: data?.nombre ?? "",
        apellido_1: data?.apellido_1 ?? "",
        apellido_2: data?.apellido_2 ?? "",
        localidad: data?.localidad ?? "",
        provincia: data?.provincia ?? "",
      };
      colegiadoCache.current.set(codigoColegiado, c);
      return c;
    } catch {
      colegiadoCache.current.set(codigoColegiado, null);
      return null;
    }
  };

  useEffect(() => {
    const prefetch = async () => {
      if (!detalle?.lotes?.length) return;

      const codes = new Set();
      for (const l of detalle.lotes) {
        for (const rc of l.recibos || []) {
          const code = rc.colegiado_codigo ?? rc.colegiadoCodigo;
          if (!code) continue;
          if (!colegiadoCache.current.has(code)) codes.add(code);
        }
      }
      const toFetch = Array.from(codes);
      if (toFetch.length === 0) return;

      const chunkSize = 15;
      for (let i = 0; i < toFetch.length; i += chunkSize) {
        const slice = toFetch.slice(i, i + chunkSize);
        const settled = await Promise.allSettled(slice.map((code) => fetchColegiadoDetalles(code)));
        setColegiadoNames((prev) => {
          const next = { ...prev };
          slice.forEach((code, idx) => {
            const res = settled[idx];
            if (res.status === "fulfilled" && res.value) next[code] = nombreCompleto(res.value);
            else if (!(code in next)) next[code] = "";
          });
          return next;
        });
      }
    };
    prefetch();
  }, [detalle]);

  const useColegiadoName = (codigo) => {
    const [name, setName] = useState(colegiadoNames[codigo] || "");
    useEffect(() => {
      let alive = true;
      const run = async () => {
        const cached = colegiadoNames[codigo];
        if (typeof cached !== "undefined") {
          if (alive) setName(cached);
          return;
        }
        const det = await fetchColegiadoDetalles(codigo);
        const full = det ? nombreCompleto(det) : "";
        if (!alive) return;
        setColegiadoNames((prev) => ({ ...prev, [codigo]: full }));
        setName(full);
      };
      if (codigo) run();
      return () => {
        alive = false;
      };
    }, [codigo]);
    return name;
  };

  // ====================== Filas ======================
  const RowRemesa = ({ r }) => {
    const nbTx = r.nb_tx ?? r.nbTx;
    const ctrl = r.ctrl_sum ?? r.ctrlSum;
    const creado = r.creado_el ?? r.creadoEl;
    const esVentanilla = (r.tipo || "").toUpperCase() === "VENTANILLA";
    return (
      <tr key={r.id} className="border-t">
        <td>{r.id}</td>
        <td>{r.tipo}</td>
        <td>{r.periodo}</td>
        <td>{r.estado}</td>
        <td className="text-left">{nbTx}</td>
        <td className="text-left">{fmtMoney(ctrl)}</td>
        <td>{new Date(creado).toLocaleString()}</td>
        <td className="flex gap-2 justify-end">
          <Button variant="ghost" iconLeft={<Eye className="w-4 h-4" />} onClick={() => verDetalle(r.id)}>
            Ver
          </Button>
          <Button
            variant="ghost"
            iconLeft={<Download className="w-4 h-4" />}
            onClick={() => descargarXML(r.id)}
            disabled={esVentanilla}
            title={esVentanilla ? "No aplica PAIN.008 en ventanilla" : "Descargar XML"}
          >
            XML
          </Button>

          {(r.tipo || "").toUpperCase() === "PARCIAL" && r.id === lastParcialId && (
            <Button variant="outline" iconLeft={<Settings2 className="w-4 h-4" />} onClick={() => actualizarParcial(r.id)}>
              Actualizar
            </Button>
          )}
          <Button variant="danger" iconLeft={<Trash2 className="w-4 h-4" />} onClick={() => eliminarRemesa(r.id)}>
            Eliminar
          </Button>
        </td>
      </tr>
    );
  };

  const ReciboRow = ({ rc }) => {
    const codigo = rc.colegiado_codigo ?? rc.colegiadoCodigo;
    const fullName = useColegiadoName(codigo);
    const deleting = deletingRcId === rc.id;

    return (
      <tr key={rc.id} className="border-t">
        <td className="text-center">{rc.id}</td>
        <td className="text-center">
          {codigo}
          {fullName ? <span className="ml-2 text-xs text-gray-500">• {fullName}</span> : null}
        </td>
        <td className="font-mono text-xs text-center">{rc.mndt_id ?? rc.mndtId}</td>
        <td>{fmtMoney(rc.importe)}</td>
        <td>{rc.periodo}</td>
        <td>
          <EstadoReciboSelect value={rc.resultado_banco ?? rc.resultadoBanco} onChange={(ui) => setResultadoRecibo(rc, ui)} />
        </td>
        <td className="text-center">
          <input
            type="checkbox"
            checked={toBool(rc.remesable, true)}
            onChange={() => toggleRemesable(rc)}
          />
        </td>

        <td className="text-right">
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" iconLeft={<Pencil className="w-4 h-4" />} onClick={() => openEditRecibo(rc)}>
              Editar
            </Button>
            <Button
              variant="danger"
              iconLeft={<Trash2 className="w-4 h-4" />}
              disabled={deleting}
              onClick={() => eliminarRecibo(rc)}
            >
              {deleting ? "..." : "Eliminar"}
            </Button>
          </div>
        </td>
      </tr>
    );
  };

  // ====================== Render ======================
  return (
    <div className="space-y-6">
      {/* Generador */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="grid md:grid-cols-3 gap-4 bg-white rounded-2xl p-4 shadow"
      >
        <div>
          <FieldLabel label="Tipo de remesa" />
          <Select value={tipo} onChange={(e) => setTipo(e.target.value)}>
            <option value="GRAL">Trimestral (adelantada)</option>
            <option value="PARCIAL">Parcial (hacia atrás)</option>
            <option value="VENTANILLA">Ventanilla (sin SEPA)</option>
          </Select>
        </div>
        <div>
          <FieldLabel label="Periodo" hint="YYYYQn" />
          <TextInput value={periodo} onChange={(e) => setPeriodo(e.target.value)} />
        </div>
        <div>
          <FieldLabel label="Fecha de cobro" />
          <TextInput type="date" value={reqdColltnDt} onChange={(e) => setReqd(e.target.value)} />
        </div>
        <div className="md:col-span-3 flex justify-end">
          <Button
            onClick={onGenerar}
            disabled={loadingGen}
            iconLeft={loadingGen ? <Loader2 className="animate-spin w-4 h-4" /> : <PlusCircle className="w-4 h-4" />}
          >
            Generar remesa
          </Button>
        </div>
      </motion.div>

      {/* ALTAS agrupadas */}
      {altasRemesas.length > 0 && (
        <div className="bg-white rounded-2xl shadow p-4">
          <div className="flex items-center justify-between">
            <div className="font-semibold">
              <span className="uppercase">ALTAS</span> (agrupadas)
            </div>
            <div className="text-sm text-gray-600">
              {altasStats.nbTx} tx · {fmtMoney(altasStats.sum)} €
            </div>
          </div>

          <div className="mt-2">
            <button
              className="text-sm text-indigo-600 hover:underline flex items-center gap-1"
              onClick={() => setExpandAltas((v) => !v)}
            >
              {expandAltas ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              {expandAltas ? "Ocultar detalle ALTAS" : "Mostrar detalle ALTAS"}
            </button>
          </div>

          {expandAltas && (
            <div className="overflow-auto mt-3">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th>ID</th>
                    <th>Tipo</th>
                    <th>Periodo</th>
                    <th>Estado</th>
                    <th className="text-left">Tx</th>
                    <th className="text-left">Importe</th>
                    <th>Creado</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>{altasRemesas.map((r) => <RowRemesa key={r.id} r={r} />)}</tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Listado remesas (sin ALTAS) */}
      <div className="bg-white rounded-2xl shadow p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-gray-500" />
          <TextInput placeholder="Buscar periodo, tipo, estado..." value={q} onChange={(e) => setQ(e.target.value)} />
          <Button variant="outline" onClick={refreshList}>
            Filtrar
          </Button>
          <div className="ml-auto">
            <Button variant="outline" iconLeft={<Download size={16} />} onClick={exportarListadoRemesas}>
              Exportar
            </Button>
          </div>
        </div>

        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th>ID</th>
                <th>Tipo</th>
                <th>Periodo</th>
                <th>Estado</th>
                <th className="text-left">Tx</th>
                <th className="text-left">Importe</th>
                <th>Creado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loadingList && (
                <tr>
                  <td colSpan={8} className="text-center">
                    Cargando…
                  </td>
                </tr>
              )}
              {!loadingList && otherRemesas.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-gray-400">
                    Sin remesas
                  </td>
                </tr>
              )}
              {!loadingList && otherRemesas.map((r) => <RowRemesa key={r.id} r={r} />)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal detalle */}
      {detalle && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-[95vw] max-w-6xl max-h-[85vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b">
              <div>
                <div className="font-semibold">
                  Remesa #{detalle.id} • {detalle.tipo} • {detalle.periodo}
                </div>

                {/* ✅ Totales recalculados desde recibos */}
                <div className="text-sm text-gray-500">
                  {detalleStats.nbTx} tx · {fmtMoney(detalleStats.sum)} € · {detalle.estado}
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" iconLeft={<Download className="w-4 h-4" />} onClick={exportarRecibosDeRemesa}>
                  Exportar
                </Button>
                <Button
                  variant="ghost"
                  iconLeft={<Download className="w-4 h-4" />}
                  onClick={() => descargarXML(detalle.id)}
                  disabled={(detalle.tipo || "").toUpperCase() === "VENTANILLA"}
                  title={(detalle.tipo || "").toUpperCase() === "VENTANILLA" ? "No aplica PAIN.008 en ventanilla" : "Descargar XML"}
                >
                  XML
                </Button>
                <Button variant="danger" iconLeft={<Trash2 className="w-4 h-4" />} onClick={() => eliminarRemesa(detalle.id)}>
                  Eliminar
                </Button>
                <Button variant="ghost" iconLeft={<X className="w-4 h-4" />} onClick={() => setDetalle(undefined)}>
                  Cerrar
                </Button>
              </div>
            </div>

            <div className="p-4 overflow-auto max-h-[70vh]">
              {loadingDet && <div className="text-center">Cargando detalle…</div>}

              {detalle && (
                <div className="p-2">
                  <button className="text-sm" onClick={() => setShowBulk(!showBulk)}>
                    <Settings2 className="inline w-4 h-4" /> {showBulk ? "Ocultar edición masiva" : "Mostrar edición masiva"}
                  </button>
                  {showBulk && (
                    <div className="mt-3 space-y-2">
                      <div className="flex gap-2">
                        <Button onClick={() => doBulkResultado("COBRADO", "ALL")}>COBRADO todos</Button>
                        <Button onClick={() => doBulkResultado("PENDIENTE", "ALL")}>PENDIENTE todos</Button>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => doBulkRemesable(1, "ALL")}>Remesable todos</Button>
                        <Button onClick={() => doBulkRemesable(0, "ALL")}>No remesable todos</Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!loadingDet &&
                detalle.lotes?.map((lote) => {
                  const loteStats = calcLoteStats(lote); // ✅ recalculado desde recibos
                  return (
                    <div key={lote.id} className="border rounded-2xl mb-4">
                      <div className="p-2 bg-gray-50 flex justify-between">
                        <span>
                          {" "}
                          Lote {lote.id} • {lote.seq_tp ?? lote.seqTp} • {lote.reqd_colltn_dt ?? lote.reqdColltnDt}
                        </span>
                        <span>
                          {loteStats.nbTx} tx · {fmtMoney(loteStats.sum)} €
                        </span>
                      </div>

                      <table className="min-w-full text-sm text-center">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="text-center">ID</th>
                            <th className="text-center">Colegiado</th>
                            <th className="text-center">Mandato</th>
                            <th>Importe</th>
                            <th>Periodo</th>
                            <th>Estado</th>
                            <th>Remesable</th>
                            <th className="text-right pr-2">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>{(lote.recibos || []).map((rc) => <ReciboRow key={rc.id} rc={rc} />)}</tbody>
                      </table>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Modal edición recibo */}
          {editOpen && editRc && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]">
              <div className="bg-white rounded-2xl shadow-xl w-[95vw] max-w-xl overflow-hidden">
                <div className="p-4 border-b flex items-center justify-between">
                  <div className="font-semibold">Editar recibo #{editRc.id}</div>
                  <button onClick={closeEditRecibo} className="p-1 hover:bg-gray-100 rounded-lg">
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="p-4 space-y-3">
                  <div className="text-sm text-gray-600">
                    Colegiado: <span className="font-medium">{editRc.colegiado_codigo}</span>
                  </div>

                  <div>
                    <FieldLabel label="Importe (€)" />
                    <TextInput
                      type="number"
                      step="0.01"
                      value={editRc.importe}
                      onChange={(e) => setEditRc((p) => ({ ...p, importe: e.target.value }))}
                    />
                  </div>

                  <div>
                    <FieldLabel label="Periodo" hint="YYYYQn" />
                    <TextInput value={editRc.periodo} onChange={(e) => setEditRc((p) => ({ ...p, periodo: e.target.value }))} />
                  </div>

                  <div>
                    <FieldLabel label="Estado (resultado banco)" />
                    <div className="flex justify-center">
                      <EstadoReciboSelect
                        value={editRc.resultado_banco}
                        onChange={(ui) => setEditRc((p) => ({ ...p, resultado_banco: ui }))}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2 justify-between">
                    <FieldLabel label="Remesable" />
                    <input
                      type="checkbox"
                      checked={toBool(editRc.remesable, true)}
                      onChange={() => setEditRc((p) => ({ ...p, remesable: !p.remesable }))}
                    />
                  </div>

                  <div>
                    <FieldLabel label="Mandato (mndt_id)" />
                    <TextInput value={editRc.mndt_id} onChange={(e) => setEditRc((p) => ({ ...p, mndt_id: e.target.value }))} />
                  </div>

                  <div>
                    <FieldLabel label="EndToEnd (end_to_end_id)" />
                    <TextInput
                      value={editRc.end_to_end_id}
                      onChange={(e) => setEditRc((p) => ({ ...p, end_to_end_id: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="p-4 border-t flex justify-end gap-2">
                  <Button variant="outline" onClick={closeEditRecibo} disabled={savingRc}>
                    Cancelar
                  </Button>
                  <Button
                    onClick={saveEditRecibo}
                    disabled={savingRc}
                    iconLeft={savingRc ? <Loader2 className="animate-spin w-4 h-4" /> : <Pencil className="w-4 h-4" />}
                  >
                    Guardar
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RemesasPanel;
